import React from "react";
import { ArrowDown, ArrowUp, BarChart3, ChevronRight, RefreshCw, Search, X } from "lucide-react";

import { getMarketIndustries, getMarketIndustryFlow, getMarketIndustryTrends } from "../../api/client";
import { Chart } from "../../components/charts";
import { industryActivityChartOption } from "./chartOptions";
import { useLatestRequest } from "./useLatestRequest";
import baseStyles from "./marketObservation.module.css";
import activityStyles from "./marketActivity.module.css";
import tableStyles from "./marketTables.module.css";

const styles = { ...baseStyles, ...tableStyles, ...activityStyles };

const METRICS = [
  ["market_turnover_share", "成交额占比"],
  ["share_change_10d_pp", "十日份额变化"],
  ["equal_weight_pct_chg_5d", "5日等权涨跌"],
  ["excess_pct_chg_5d", "相对沪深300超额"],
  ["volume_ratio_20d", "20日量比"],
  ["up_ratio", "上涨家数占比"],
];

const TABLE_COLUMNS = [
  ["equal_weight_pct_chg", "今日涨跌"],
  ["equal_weight_pct_chg_5d", "5日涨跌"],
  ["market_turnover_share", "成交额占比"],
  ["share_change_10d_pp", "十日份额变化"],
  ["volume_ratio_20d", "20日量比"],
  ["up_ratio", "上涨占比"],
  ["main_net_inflow", "大单净额"],
];

function value(raw, digits = 2, suffix = "") {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? `${parsed.toFixed(digits)}${suffix}` : "--";
}

function signed(raw, digits = 2, suffix = "%") {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? `${parsed > 0 ? "+" : ""}${parsed.toFixed(digits)}${suffix}` : "--";
}

function tone(raw) {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed === 0) return styles.flat;
  return parsed > 0 ? styles.up : styles.down;
}

export function IndustryActivityPage({ navigate, onStatus, snapshotId }) {
  const [level, setLevel] = React.useState("L1");
  const [rangeDays, setRangeDays] = React.useState(120);
  const [metric, setMetric] = React.useState("market_turnover_share");
  const [selected, setSelected] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState({ key: "main_net_inflow", order: "desc" });
  const [payload, setPayload] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [flowContract, setFlowContract] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const selectedRef = React.useRef([]);
  const requestGuard = useLatestRequest();

  const load = React.useCallback(async (selection = selectedRef.current) => {
    const requestVersion = requestGuard.start();
    setLoading(true);
    setError("");
    try {
      const [trends, industries, flow] = await Promise.all([
        getMarketIndustryTrends({ snapshotId, level, metric, rangeDays, selected: selection }),
        getMarketIndustries({ snapshotId, level, query: "", sort: "equal_weight_pct_chg", order: "desc" }),
        getMarketIndustryFlow({ snapshotId, level }),
      ]);
      const flowById = new Map((flow.items ?? []).map((item) => [item.industry_id, item]));
      const merged = (industries.items ?? []).map((item) => ({ ...item, ...flowById.get(item.industry_id) }));
      if (!requestGuard.isCurrent(requestVersion)) return;
      setRows(merged);
      setPayload(trends);
      setFlowContract({
        factDate: flow.data_date,
        pageDate: flow.page_date,
        disclosureLag: flow.disclosure_lag,
      });
      if (!selection.length) {
        selectedRef.current = trends.data.selected;
        setSelected(trends.data.selected);
      }
      onStatus({ status: "ok", dataDate: trends.meta.business_date });
    } catch (requestError) {
      if (!requestGuard.isCurrent(requestVersion)) return;
      setError(requestError.message);
      onStatus({ status: "unavailable", dataDate: null });
    } finally {
      if (requestGuard.isCurrent(requestVersion)) setLoading(false);
    }
  }, [level, metric, onStatus, rangeDays, requestGuard, snapshotId]);

  React.useEffect(() => { void load(); }, [load]);
  React.useEffect(() => { selectedRef.current = selected; }, [selected]);

  function changeLevel(nextLevel) {
    selectedRef.current = [];
    setSelected([]);
    setLevel(nextLevel);
  }

  function toggleSelected(industryId) {
    const current = selectedRef.current;
    if (!current.includes(industryId) && current.length >= 12) return;
    const next = current.includes(industryId)
      ? current.filter((item) => item !== industryId)
      : [...current, industryId];
    selectedRef.current = next;
    setSelected(next);
    void load(next);
  }

  function changeSort(key) {
    setSort((current) => ({ key, order: current.key === key && current.order === "desc" ? "asc" : "desc" }));
  }

  const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
  const visibleRows = rows
    .filter((item) => !normalizedQuery || `${item.industry_name} ${item.industry_code}`.toLocaleLowerCase("zh-CN").includes(normalizedQuery))
    .sort((left, right) => {
      const a = left[sort.key];
      const b = right[sort.key];
      if (sort.key === "name") return String(a ?? left.industry_name).localeCompare(String(b ?? right.industry_name), "zh-CN") * (sort.order === "asc" ? 1 : -1);
      return ((Number(a) || 0) - (Number(b) || 0)) * (sort.order === "asc" ? 1 : -1);
    });
  const nameById = new Map(rows.map((item) => [item.industry_id, item.industry_name]));
  const selectionLimitReached = selected.length >= 12;

  if (loading && !payload) return <div className={styles.statePanel}><RefreshCw className={styles.spin} size={22} /><strong>正在读取行业基础指标</strong><span>全部指标固定到当前已发布市场快照。</span></div>;
  if (error && !payload) return <div className={styles.statePanel}><BarChart3 size={22} /><strong>行业资金暂时无法读取</strong><span>{error}</span><button className={styles.primaryButton} onClick={load}>重新读取</button></div>;

  return (
    <div className={`${styles.page} ${styles.industryActivityPage}`}>
      <div className={styles.activityPageHeading}>
        <div className={styles.activityPageNav}>
          <h1>行业资金</h1>
          <span className={styles.resultCount}>
            资金事实日 {flowContract?.factDate ?? "--"}
            {flowContract?.disclosureLag === "T+1" ? " · T+1 披露" : ""}
          </span>
          <nav aria-label="行业资金视图"><button onClick={() => navigate("/industry")}>行业涨跌</button><button className={styles.active}>趋势对比</button></nav>
        </div>
        <div className={styles.segmented}>{["L1", "L2"].map((item) => <button key={item} className={level === item ? styles.active : ""} aria-pressed={level === item} onClick={() => changeLevel(item)}>申万{item === "L1" ? "一级" : "二级"}</button>)}</div>
      </div>

      <section className={`${styles.card} ${styles.activityChartCard}`}>
        <div className={styles.activityToolbar}>
          <div><strong>趋势对比</strong><span>已选 {selected.length}/12 个行业</span></div>
          <div className={styles.toolbarControls}>
            <div className={styles.metricTabs}>{METRICS.map(([key, label]) => <button key={key} className={metric === key ? styles.activeMetric : ""} aria-pressed={metric === key} onClick={() => setMetric(key)}>{label}</button>)}</div>
            <div className={styles.periodTabs}>{[60, 120, 240].map((days) => <button key={days} className={rangeDays === days ? styles.active : ""} aria-pressed={rangeDays === days} onClick={() => setRangeDays(days)}>{days}日</button>)}</div>
            <button className={styles.iconButton} onClick={() => void load()} aria-label="刷新数据"><RefreshCw className={loading ? styles.spin : ""} size={16} /></button>
          </div>
        </div>
        <div className={styles.selectedChips}>{selected.map((industryId) => <button key={industryId} onClick={() => toggleSelected(industryId)}>{nameById.get(industryId) ?? industryId}<X size={12} /></button>)}</div>
        {selectionLimitReached && <p className={styles.selectionLimitNotice} role="status">已选满 12 个行业；请先移除一个行业再添加。</p>}
        <div aria-busy={loading}><Chart option={industryActivityChartOption(payload?.data, metric)} emptyText="请选择要对比的行业" /></div>
      </section>

      <section className={styles.tableCard}>
        <div className={styles.tableToolbar}>
          <div><strong>全部行业</strong><span className={styles.resultCount}>只展示可复算的成交、涨跌、宽度与大单资金指标。</span></div>
          <label className={styles.searchBox}><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索行业名称或代码" />{query && <button onClick={() => setQuery("")} aria-label="清空搜索"><X size={13} /></button>}</label>
        </div>
        <div className={styles.tableScroll}>
          <table className={`${styles.industryTable} ${styles.activityTable}`}>
            <thead><tr><th>排名</th><th>行业</th>{TABLE_COLUMNS.map(([key, label]) => <th key={key} aria-sort={sort.key === key ? (sort.order === "asc" ? "ascending" : "descending") : "none"}><button onClick={() => changeSort(key)}>{label}{sort.key === key && (sort.order === "desc" ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}</button></th>)}</tr></thead>
            <tbody>{visibleRows.map((item, index) => <tr key={item.industry_id} className={selected.includes(item.industry_id) ? styles.selectedRow : ""}>
              <td>{index + 1}</td>
              <td><div className={styles.industryNameCell}><button className={styles.selectionButton} disabled={item.metric_applicability === "not_applicable_no_members" || (selectionLimitReached && !selected.includes(item.industry_id))} title={item.metric_applicability === "not_applicable_no_members" ? "当前快照无有效成分，不参与趋势对比" : selectionLimitReached && !selected.includes(item.industry_id) ? "已选满 12 个行业，请先移除一个行业" : undefined} onClick={() => toggleSelected(item.industry_id)} aria-pressed={selected.includes(item.industry_id)}><span />{item.industry_name}<small>{item.industry_code}</small></button><button className={styles.detailButton} onClick={() => navigate(`/industry/${item.industry_code}`)} aria-label={`查看${item.industry_name}详情`}><ChevronRight size={14} /></button></div></td>
              <td className={tone(item.equal_weight_pct_chg)}>{signed(item.equal_weight_pct_chg)}</td>
              <td className={tone(item.equal_weight_pct_chg_5d)}>{signed(item.equal_weight_pct_chg_5d)}</td>
              <td>{value(item.market_turnover_share, 2, "%")}</td>
              <td className={tone(item.share_change_10d_pp)}>{signed(item.share_change_10d_pp, 2, "pp")}</td>
              <td>{value(item.volume_ratio_20d, 2, "x")}</td>
              <td>{value(item.up_ratio, 1, "%")}</td>
              <td className={tone(item.main_net_inflow)}>{signed(Number(item.main_net_inflow) / 100000000, 2, " 亿")}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>
      <footer className={styles.evidenceFooter}><span>页面日期：{flowContract?.pageDate ?? payload?.meta?.business_date}</span><span>资金事实日：{flowContract?.factDate ?? "--"}</span><span>指标：{payload?.data?.metric_contract?.label}</span><span>不包含私有评分或交易建议。</span></footer>
    </div>
  );
}
